import React, { useState } from 'react';
import { useIonRouter } from '@ionic/react';
import {
  startShareSession,
  type ReshareUpdatesStart,
} from '@/modules/share/session_storage';
import { Destinations, routesFor } from '@/modules/share/navigation_logic';
import { useAppConfig } from '@/modules/config';
import { topicGardenEnabled } from '@/conditional_feature/topic_garden_enabled';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { UnlockFlow } from '@/hooks/useActionGuard';
import { introLimitReached } from '../intro_limit';
import { TopicLogic } from '../logic';
import type { Topic } from '../types';
import WelcomeButton from './WelcomeButton';

const SHARE_HELP_TEXT = {
  singleVerification: 'Verify yourself to share this topic',
  updateProfile: 'Update your profile to share this topic',
  setupProfile: 'Set up your profile to share this topic',
};

// The unlock flows whose destination offers a button back to where the user
// came from (the Profile Wizard's Attribution step, reached directly or at the
// end of the wizard). The wizard opens under them, so that button resumes the
// reshare rather than dropping the user back on the topic.
const RETURNING_UNLOCK_FLOWS: ReadonlySet<UnlockFlow> = new Set([
  UnlockFlow.ProfileWizardStart,
  UnlockFlow.ProfileWizardAttribution,
]);

// The unlock flows that open as a modal of the guard's own, inside the sheet,
// so the sheet stays up under them.
const MODAL_UNLOCK_FLOWS: ReadonlySet<UnlockFlow> = new Set([
  UnlockFlow.PhoneVerificationModal,
  UnlockFlow.EmailVerificationModal,
]);

interface ResharePromptBlockProps {
  topic: Topic;
  topicId: string;
  /**
   * The leg to pass on — `?legId=`, when the visitor arrived on one. With it the
   * wizard opens in reshare mode and the legs it sends are recorded as that
   * leg's children, so the run's chain of who-passed-it-to-whom keeps growing.
   * Without one it is a fresh share, which starts a run of its own.
   *
   * Every type passes its leg in. The two reshare story fields
   * (`allowedSenderName`, `connectionToPrevLeg`) are optional, so a wizard that
   * never asks for them still sends, and the story reads through the server's
   * defaults; `GET /api/sharing-legs/{id}/reshare-story`, which
   * `ComposeWithTemplate` waits on, answers for every type (#602).
   */
  previousLegId?: string;
  /**
   * The start that passes on the update that brought the visitor here, or
   * null. When its leg is the one being passed on, the reshare carries the
   * update too.
   */
  receivedUpdateReshare?: ReshareUpdatesStart | null;
  /** Close the sheet before the wizard pushes over it. */
  onDismiss: () => void;
}

/**
 * The welcome sheet's "pass it on" button: someone who has just received a
 * topic can start sharing it from the sheet itself, rather than dismissing it
 * and finding the share dock underneath.
 *
 * Nothing is offered when the topic's sharing is closed to this visitor — its
 * intro limit is spent, or only its listing managers may share it — so the
 * button never leads to a wizard that will refuse to send.
 *
 * The button sits behind the same `share:topics` guard as the share dock, so a
 * visitor who still owes something before they may share (a verification, or
 * the Familiar Name on their profile) gets the toast that says what instead of
 * entering the flow. The toast appears over the sheet, which stays up until the
 * visitor takes its button. When that button leads to the profile, the sheet
 * closes and the wizard opens on its recipients step underneath the profile,
 * so the profile's "Back to resharing" picks the reshare up where it began.
 */
const ResharePromptBlock: React.FC<ResharePromptBlockProps> = ({
  topic,
  topicId,
  previousLegId,
  receivedUpdateReshare = null,
  onDismiss,
}) => {
  const router = useIonRouter();
  const { features } = useAppConfig();
  const [starting, setStarting] = useState(false);

  const closed =
    topic.onlyManagersCanShare ||
    introLimitReached(
      topic.introCount,
      topic.introCountLimit,
      features.introCountMax
    );
  if (closed) return null;

  const shareRoute = `/topics/${encodeURIComponent(topicId)}/share`;

  const startSession = () => {
    // Passes on the leg the visitor arrived on, when there is one, with the
    // update it carried when it was one. Without a leg it starts the same
    // session the share dock's own button starts, which drops a recipient
    // selection abandoned from an earlier share (#479).
    const passesOnReceivedUpdate =
      !!previousLegId &&
      receivedUpdateReshare?.reshare.previousLegId === previousLegId;
    return startShareSession(
      topicId,
      passesOnReceivedUpdate
        ? receivedUpdateReshare
        : {
            reshare: previousLegId ? { previousLegId } : undefined,
            typeDefaultsToExcludingAware:
              TopicLogic.defaultsToExcludingAwareRecipients(topic.type),
          }
    );
  };

  const handleClick = async () => {
    if (starting) return;
    setStarting(true);
    await startSession();
    onDismiss();
    router.push(shareRoute, 'forward', 'push');
  };

  // Runs when the guard's toast button is tapped, before the guard sends the
  // visitor to whatever unlocks sharing.
  const handleUnlock = async (unlockFlow: UnlockFlow) => {
    if (MODAL_UNLOCK_FLOWS.has(unlockFlow)) return;
    if (RETURNING_UNLOCK_FLOWS.has(unlockFlow)) {
      await startSession();
      onDismiss();
      // Straight to the recipients step rather than through the share route's
      // redirect, which resolves asynchronously and would replace the profile
      // pushed next instead of this page. A forward push, so the router
      // records the topic beneath it and the wizard's way out still lands
      // there after "Back to resharing".
      router.push(
        routesFor(topicGardenEnabled(features) ? 'garden' : 'wizard').pathFor(
          topicId,
          Destinations.recipients,
          ''
        ),
        'forward',
        'push'
      );
      return;
    }
    onDismiss();
  };

  return (
    <ActionGuardContainer>
      <ActionGuard
        disableMode="on-demand"
        action={ACTIONS.SHARE}
        resource={RESOURCES.TOPICS}
        resourceId={topicId}
        helpText={SHARE_HELP_TEXT}
        returnLabel="Back to resharing"
        onUnlock={handleUnlock}
        guardedElem={
          <WelcomeButton
            label="Keep the ball rolling & reshare"
            onClick={handleClick}
            disabled={starting}
            testId="welcome-keep-rolling"
          />
        }
      />
    </ActionGuardContainer>
  );
};

export default ResharePromptBlock;
